/**
 * Who is signed in, and signing out (ORDR-204).
 *
 * MEASURED IN PRODUCTION (2026-10-11): the staff app said nowhere who was signed
 * in and offered no way out, so the operator closed the browser to change from
 * a platform-admin account to the reseller's staff account.
 *
 * The control is the framework's `PaioAuthButton`: the person's name, and a
 * "Sign out" item that calls `onSignOut`. It is not hand-rolled. The framework
 * component calls no endpoint, so `onSignOut` ends the shared session through
 * this app's gateway client (`../auth/signOut.ts`) and, once it has ended,
 * lands on sign-in with a document load, so nothing of the previous account
 * survives in memory.
 *
 * The name is the SESSION's user, read by the staff door from `/auth/session`,
 * and it changes when the session does.
 */

import { useCallback, useState, type ReactElement } from "react";
import { PaioAuthButton } from "@pirouz-ca/ui-components";
import type { ApiClient } from "../api/client.js";
import { LOGIN_PATH } from "../auth/paths.js";
import { signOut } from "../auth/signOut.js";

export interface AccountMenuProps {
  readonly client: ApiClient;
  /** The signed-in person's name, from the session. */
  readonly name: string;
  /** A document load. Injected for tests. */
  readonly navigate: (href: string) => void;
}

export function AccountMenu({ client, name, navigate }: AccountMenuProps): ReactElement {
  const [failure, setFailure] = useState<string | null>(null);

  const onSignOut = useCallback((): void => {
    setFailure(null);
    void signOut(client).then((outcome) => {
      if (outcome.kind === "signed-out") {
        navigate(LOGIN_PATH);
      } else {
        setFailure(outcome.correlationId);
      }
    });
  }, [client, navigate]);

  return (
    <div data-testid="signed-in-as" className="flex flex-wrap items-center gap-2 p-2 text-sm">
      <span className="text-[var(--color-neutral-600)]">Signed in as</span>
      <PaioAuthButton isAuthenticated userName={name} onSignIn={() => navigate(LOGIN_PATH)} onSignOut={onSignOut} />
      {failure !== null && (
        <p role="alert" data-testid="sign-out-error" className="text-[var(--color-neutral-600)]">
          We could not sign you out. You are still signed in. Reference: {failure}
        </p>
      )}
    </div>
  );
}
